import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import {
    prepareReportModalBrands,
    prepareReportModalProducts,
    type ReportModalAspectRadarPoint,
} from '@/domain/reporting/reporting-modal-aggregations';
import {
    type ReportModalAverageRatingChartData,
    type ReportModalRatingDistributionChartData,
    type ReportModalResponseTimeChartData,
} from '@/domain/reporting/reporting-modal-charts';
import { buildReportModalBindings } from '@/domain/reporting/reporting-modal-bindings';
import { createEmptyReportModalSummaryData } from '@/domain/reporting/reporting-modal-summary';
import { getDateDaysAgoIso, toIsoDate } from '@/domain/reporting/reporting-shared.formatters';
import { type TreemapLayoutItem } from '@/domain/reporting/reporting-shared.types';
import { AibackReportAspectRowEntity, AibackReportPdfTopItemEntity } from '@/entities/aiback-report.entity';
import { useProductsReportModalCompanyName } from '@/components/products-report-modal/use-products-report-modal-company-name';
import { useProductsReportModalExportPdf } from '@/components/products-report-modal/use-products-report-modal-export-pdf';
import { useProductsReportModalSummary } from '@/components/products-report-modal/use-products-report-modal-summary';
import {
    buildReportBrandGroupOptions,
    buildReportPlatformOptions,
    buildReportProductGroupOptions,
    normalizeNextReportFilterSelection,
    reconcileReportFilterSelection,
    REPORT_FILTER_ALL_VALUE,
    resolveReportFilterSelectionLabels,
    resolveAppliedReportFilterValues,
} from '@/components/products-report-modal/report-modal-filters';
import { SelectOption } from '@/ui/Select';
import { RuntimeSupportedMarketplace } from '@/shared/marketplace';
import type { ActionButtonsActiveTable } from '@/components/action-buttons/action-buttons-view.types';

type UseProductsReportModalControllerParams = {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
    activeTable: ActionButtonsActiveTable;
    accessToken: string;
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    summaryRefreshToken: number;
    selectedReviewFeedItemIds: string[];
    selectedProductGroupIds: string[];
    selectedBrandGroupIds: string[];
};

type AspectRadarPoint = ReportModalAspectRadarPoint;

const resolveOpeningGroupFilterValues = (selectedIds: string[], availableValues: string[]): string[] => {
    const availableValueSet = new Set(availableValues);
    const normalizedSelectedIds = [...new Set(selectedIds.map((value) => value.trim()).filter(Boolean))].filter(
        (value) => availableValueSet.has(value),
    );

    return normalizedSelectedIds.length ? normalizedSelectedIds : [REPORT_FILTER_ALL_VALUE];
};

const resolveOpeningSelectedReviewFeedItemIds = (selectedIds: string[]): string[] => [
    ...new Set(selectedIds.map((value) => value.trim()).filter(Boolean)),
];

export type ProductsReportModalControllerBindings = {
    companyName: string;
    hasSelectedReviewFilter: boolean;
    selectedReviewFilterCount: number;
    clearSelectedReviewFilter: () => void;
    staleDaysCount: number;
    freshRollupDaysCount: number;
    dirtyRollupDaysCount: number;
    generatedFromScratchDaysCount: number;
    summaryProcessedDays: number;
    summaryTotalDays: number;
    periodFrom: string;
    periodTo: string;
    setPeriodFrom: (value: string) => void;
    setPeriodTo: (value: string) => void;
    platformOptions: SelectOption[];
    selectedPlatformValues: string[];
    setSelectedPlatformValues: (values: string[]) => void;
    productGroupOptions: SelectOption[];
    selectedProductGroupValues: string[];
    setSelectedProductGroupValues: (values: string[]) => void;
    brandGroupOptions: SelectOption[];
    selectedBrandGroupValues: string[];
    setSelectedBrandGroupValues: (values: string[]) => void;
    canApplyMaxPeriod: boolean;
    applyMaxPeriod: () => void;
    totalReviews: number;
    totalAnswers: number;
    answersShare: number | null;
    averageResponseTimeMinutes: number | null;
    answeredReviewsCount: number;
    totalAiAnalyses: number;
    companyAverageRating: number | null;
    ratingDistributionChartData: ReportModalRatingDistributionChartData | null;
    averageRatingChartData: ReportModalAverageRatingChartData | null;
    responseTimeChartData: ReportModalResponseTimeChartData | null;
    channelsCount: number;
    productsCount: number;
    brandsCount: number;
    topChannels: AibackReportPdfTopItemEntity[];
    topProducts: AibackReportPdfTopItemEntity[];
    topBrands: AibackReportPdfTopItemEntity[];
    treemapLayout: TreemapLayoutItem[];
    aspectRows: AibackReportAspectRowEntity[];
    aspectRadarPoints: AspectRadarPoint[];
    positiveHighlights: string[];
    positiveHighlightsHiddenCount?: number;
    negativeHighlights: string[];
    negativeHighlightsHiddenCount?: number;
    neutralHighlights?: string[];
    neutralHighlightsHiddenCount?: number;
    isSummaryLoaded: boolean;
    isExportingPdf: boolean;
    exportPdf: () => Promise<void>;
    closeModal: () => void;
};

export const useProductsReportModalController = ({
    isShown,
    setIsShown,
    activeTable,
    accessToken,
    products,
    brandGroups,
    summaryRefreshToken,
    selectedReviewFeedItemIds: openingSelectedReviewFeedItemIds,
    selectedProductGroupIds: openingSelectedProductGroupIds,
    selectedBrandGroupIds: openingSelectedBrandGroupIds,
}: UseProductsReportModalControllerParams): ProductsReportModalControllerBindings => {
    const emptySummaryData = useMemo(() => createEmptyReportModalSummaryData(), []);
    const wasShownRef = useRef(false);
    const selectedReviewFilterWasTouchedRef = useRef(false);
    const openingSelectedReviewFeedItemIdsRef = useRef<string[]>([]);

    const [periodFrom, setPeriodFrom] = useState(() => getDateDaysAgoIso(30));
    const [periodTo, setPeriodTo] = useState(() => toIsoDate(new Date()));
    const [selectedPlatformValues, setSelectedPlatformValuesState] = useState<string[]>(() => [
        REPORT_FILTER_ALL_VALUE,
    ]);
    const [selectedProductGroupValues, setSelectedProductGroupValuesState] = useState<string[]>(() => [
        REPORT_FILTER_ALL_VALUE,
    ]);
    const [selectedBrandGroupValues, setSelectedBrandGroupValuesState] = useState<string[]>(() => [
        REPORT_FILTER_ALL_VALUE,
    ]);
    const [selectedReviewFeedItemIds, setSelectedReviewFeedItemIds] = useState<string[]>([]);

    const platformOptions = useMemo(() => buildReportPlatformOptions(), []);
    const productGroupOptions = useMemo(() => buildReportProductGroupOptions(products), [products]);
    const brandGroupOptions = useMemo(() => buildReportBrandGroupOptions(brandGroups), [brandGroups]);

    const selectablePlatformValues = useMemo(
        () =>
            platformOptions.filter((option) => option.value !== REPORT_FILTER_ALL_VALUE).map((option) => option.value),
        [platformOptions],
    );
    const selectableProductGroupValues = useMemo(
        () =>
            productGroupOptions
                .filter((option) => option.value !== REPORT_FILTER_ALL_VALUE)
                .map((option) => option.value),
        [productGroupOptions],
    );
    const selectableBrandGroupValues = useMemo(
        () =>
            brandGroupOptions
                .filter((option) => option.value !== REPORT_FILTER_ALL_VALUE)
                .map((option) => option.value),
        [brandGroupOptions],
    );

    const setSelectedPlatformValues = useCallback(
        (nextValues: string[]) => {
            setSelectedPlatformValuesState((currentValues) =>
                normalizeNextReportFilterSelection({
                    currentValues,
                    nextValues,
                    availableValues: selectablePlatformValues,
                }),
            );
        },
        [selectablePlatformValues],
    );
    const setSelectedProductGroupValues = useCallback(
        (nextValues: string[]) => {
            setSelectedProductGroupValuesState((currentValues) =>
                normalizeNextReportFilterSelection({
                    currentValues,
                    nextValues,
                    availableValues: selectableProductGroupValues,
                }),
            );
        },
        [selectableProductGroupValues],
    );
    const setSelectedBrandGroupValues = useCallback(
        (nextValues: string[]) => {
            setSelectedBrandGroupValuesState((currentValues) =>
                normalizeNextReportFilterSelection({
                    currentValues,
                    nextValues,
                    availableValues: selectableBrandGroupValues,
                }),
            );
        },
        [selectableBrandGroupValues],
    );

    useEffect(() => {
        setSelectedPlatformValuesState((currentValues) =>
            reconcileReportFilterSelection({
                currentValues,
                availableValues: selectablePlatformValues,
            }),
        );
    }, [selectablePlatformValues]);

    useEffect(() => {
        setSelectedProductGroupValuesState((currentValues) =>
            reconcileReportFilterSelection({
                currentValues,
                availableValues: selectableProductGroupValues,
            }),
        );
    }, [selectableProductGroupValues]);

    useEffect(() => {
        setSelectedBrandGroupValuesState((currentValues) =>
            reconcileReportFilterSelection({
                currentValues,
                availableValues: selectableBrandGroupValues,
            }),
        );
    }, [selectableBrandGroupValues]);

    useLayoutEffect(() => {
        const isOpening = isShown && !wasShownRef.current;
        wasShownRef.current = isShown;

        if (!isOpening) {
            return;
        }

        selectedReviewFilterWasTouchedRef.current = false;
        const nextOpeningSelectedReviewFeedItemIds = resolveOpeningSelectedReviewFeedItemIds(
            openingSelectedReviewFeedItemIds,
        );
        openingSelectedReviewFeedItemIdsRef.current = nextOpeningSelectedReviewFeedItemIds;
        setSelectedReviewFeedItemIds(nextOpeningSelectedReviewFeedItemIds);
        setSelectedProductGroupValuesState(
            activeTable === 'products'
                ? resolveOpeningGroupFilterValues(openingSelectedProductGroupIds, selectableProductGroupValues)
                : [REPORT_FILTER_ALL_VALUE],
        );
        setSelectedBrandGroupValuesState(
            activeTable === 'brands'
                ? resolveOpeningGroupFilterValues(openingSelectedBrandGroupIds, selectableBrandGroupValues)
                : [REPORT_FILTER_ALL_VALUE],
        );
    }, [
        activeTable,
        isShown,
        openingSelectedReviewFeedItemIds,
        openingSelectedBrandGroupIds,
        openingSelectedProductGroupIds,
        selectableBrandGroupValues,
        selectableProductGroupValues,
    ]);

    useEffect(() => {
        if (!isShown || selectedReviewFilterWasTouchedRef.current) {
            return;
        }

        if (openingSelectedReviewFeedItemIdsRef.current.length > 0) {
            return;
        }

        const nextOpeningSelectedReviewFeedItemIds = resolveOpeningSelectedReviewFeedItemIds(
            openingSelectedReviewFeedItemIds,
        );

        if (!nextOpeningSelectedReviewFeedItemIds.length) {
            return;
        }

        openingSelectedReviewFeedItemIdsRef.current = nextOpeningSelectedReviewFeedItemIds;
        setSelectedReviewFeedItemIds((currentSelectedReviewFeedItemIds) =>
            currentSelectedReviewFeedItemIds.length > 0
                ? currentSelectedReviewFeedItemIds
                : nextOpeningSelectedReviewFeedItemIds,
        );
    }, [isShown, openingSelectedReviewFeedItemIds]);

    const selectedPlatforms = useMemo(
        () =>
            resolveAppliedReportFilterValues(
                selectedPlatformValues.filter(
                    (value): value is RuntimeSupportedMarketplace => value !== REPORT_FILTER_ALL_VALUE,
                ),
            ),
        [selectedPlatformValues],
    );
    const selectedProductGroupIds = useMemo(
        () => resolveAppliedReportFilterValues(selectedProductGroupValues),
        [selectedProductGroupValues],
    );
    const selectedBrandGroupIds = useMemo(
        () => resolveAppliedReportFilterValues(selectedBrandGroupValues),
        [selectedBrandGroupValues],
    );
    const hasSelectedReviewFilter = selectedReviewFeedItemIds.length > 0;
    const clearSelectedReviewFilter = useCallback(() => {
        selectedReviewFilterWasTouchedRef.current = true;
        setSelectedReviewFeedItemIds([]);
    }, []);
    const selectedPlatformLabels = useMemo(
        () =>
            resolveReportFilterSelectionLabels({
                options: platformOptions,
                selectedValues: selectedPlatformValues,
            }),
        [platformOptions, selectedPlatformValues],
    );
    const selectedProductLabels = useMemo(
        () =>
            resolveReportFilterSelectionLabels({
                options: productGroupOptions,
                selectedValues: selectedProductGroupValues,
            }),
        [productGroupOptions, selectedProductGroupValues],
    );
    const selectedBrandLabels = useMemo(
        () =>
            resolveReportFilterSelectionLabels({
                options: brandGroupOptions,
                selectedValues: selectedBrandGroupValues,
            }),
        [brandGroupOptions, selectedBrandGroupValues],
    );

    const companyName = useProductsReportModalCompanyName({
        isShown,
        accessToken,
    });
    const {
        averageResponseTimeMinutes,
        answeredReviewsCount,
        staleDaysCount,
        freshRollupDaysCount,
        dirtyRollupDaysCount,
        generatedFromScratchDaysCount,
        summaryProcessedDays,
        summaryTotalDays,
        firstReviewDate,
        periodAverageRating,
        averageRatingTimeline,
        averageResponseTimeTimeline,
        aspectRows,
        positiveHighlights,
        positiveHighlightsHiddenCount,
        negativeHighlights,
        negativeHighlightsHiddenCount,
        neutralHighlights,
        neutralHighlightsHiddenCount,
        periodTotalReviews,
        periodTotalAnswers,
        periodTotalAiAnalyses,
        periodChannelsCount,
        periodProductsCount,
        periodBrandsCount,
        periodTopChannels,
        periodTopProducts,
        periodTopBrands,
        periodRatingBuckets,
        periodTreemapItems,
        isSummaryLoaded,
    } = useProductsReportModalSummary({
        isShown,
        accessToken,
        periodFrom,
        periodTo,
        selectedPlatforms,
        selectedProductGroupIds,
        selectedBrandGroupIds,
        selectedReviewFeedItemIds: hasSelectedReviewFilter ? selectedReviewFeedItemIds : undefined,
        emptySummaryData,
        refreshToken: summaryRefreshToken,
    });

    const preparedProducts = useMemo(() => prepareReportModalProducts(products), [products]);
    const preparedBrands = useMemo(() => prepareReportModalBrands(brandGroups), [brandGroups]);

    const {
        totalReviews,
        totalAnswers,
        answersShare,
        totalAiAnalyses,
        channelsCount,
        productsCount,
        brandsCount,
        companyAverageRating,
        topChannels,
        topProducts,
        topBrands,
        ratingBuckets,
        ratingDistributionChartData,
        averageRatingChartData,
        responseTimeChartData,
        treemapItems,
        treemapLayout,
        aspectRadarPoints,
    } = useMemo(
        () =>
            buildReportModalBindings({
                isShown,
                isSummaryLoaded,
                preparedProducts,
                preparedBrands,
                averageRatingTimeline,
                averageResponseTimeTimeline,
                aspectRows,
                periodAverageRating,
                periodTotalReviews,
                periodTotalAnswers,
                periodTotalAiAnalyses,
                periodChannelsCount,
                periodProductsCount,
                periodBrandsCount,
                periodTopChannels,
                periodTopProducts,
                periodTopBrands,
                periodRatingBuckets,
                periodTreemapItems,
            }),
        [
            aspectRows,
            averageRatingTimeline,
            averageResponseTimeTimeline,
            isShown,
            isSummaryLoaded,
            periodAverageRating,
            periodChannelsCount,
            periodBrandsCount,
            periodProductsCount,
            periodRatingBuckets,
            periodTopChannels,
            periodTopBrands,
            periodTopProducts,
            periodTotalAiAnalyses,
            periodTotalAnswers,
            periodTotalReviews,
            periodTreemapItems,
            preparedBrands,
            preparedProducts,
        ],
    );

    const { isExportingPdf, exportPdf } = useProductsReportModalExportPdf({
        accessToken,
        companyName,
        periodFrom,
        periodTo,
        selectedPlatformLabels,
        selectedProductLabels,
        selectedBrandLabels,
        totalReviews,
        totalAnswers,
        answersShare,
        averageResponseTimeMinutes,
        answeredReviewsCount,
        totalAiAnalyses,
        companyAverageRating,
        channelsCount,
        productsCount,
        brandsCount,
        topChannels,
        topProducts,
        topBrands,
        ratingBuckets,
        averageRatingTimeline,
        averageResponseTimeTimeline,
        treemapItems,
        aspectRows,
        positiveHighlights,
        positiveHighlightsHiddenCount,
        negativeHighlights,
        negativeHighlightsHiddenCount,
        neutralHighlights,
        neutralHighlightsHiddenCount,
    });

    const closeModal = useCallback(() => {
        setIsShown(false);
    }, [setIsShown]);

    const applyMaxPeriod = useCallback(() => {
        if (!firstReviewDate) {
            return;
        }

        setPeriodFrom(firstReviewDate);
        setPeriodTo(toIsoDate(new Date()));
    }, [firstReviewDate]);

    return {
        companyName,
        hasSelectedReviewFilter,
        selectedReviewFilterCount: selectedReviewFeedItemIds.length,
        clearSelectedReviewFilter,
        staleDaysCount,
        freshRollupDaysCount,
        dirtyRollupDaysCount,
        generatedFromScratchDaysCount,
        summaryProcessedDays,
        summaryTotalDays,
        periodFrom,
        periodTo,
        platformOptions,
        selectedPlatformValues,
        setSelectedPlatformValues,
        productGroupOptions,
        selectedProductGroupValues,
        setSelectedProductGroupValues,
        brandGroupOptions,
        selectedBrandGroupValues,
        setSelectedBrandGroupValues,
        setPeriodFrom,
        setPeriodTo,
        canApplyMaxPeriod: !!firstReviewDate,
        applyMaxPeriod,
        totalReviews,
        totalAnswers,
        answersShare,
        averageResponseTimeMinutes,
        answeredReviewsCount,
        totalAiAnalyses,
        companyAverageRating,
        ratingDistributionChartData,
        averageRatingChartData,
        responseTimeChartData,
        channelsCount,
        productsCount,
        brandsCount,
        topChannels,
        topProducts,
        topBrands,
        treemapLayout,
        aspectRows,
        aspectRadarPoints,
        positiveHighlights,
        positiveHighlightsHiddenCount,
        negativeHighlights,
        negativeHighlightsHiddenCount,
        neutralHighlights,
        neutralHighlightsHiddenCount,
        isSummaryLoaded,
        isExportingPdf,
        exportPdf,
        closeModal,
    };
};
